import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, AlertCircle } from 'lucide-react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import AuthLayout from '../components/AuthLayout';
import Input from '../components/ui/Input';
import Logo from '../components/Logo';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [validations, setValidations] = useState({
    length: false,
    english: false,
    numberOrSymbol: false
  });

  useEffect(() => {
    setValidations({
      length: password.length >= 8,
      english: /[a-zA-Z]/.test(password),
      numberOrSymbol: /[0-9!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  }, [password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed || isLoading) return;
    setError(null);
    
    if (!validations.length || !validations.english || !validations.numberOrSymbol) {
        setError('パスワードの要件を満たしてください');
        return;
    }

    setIsLoading(true);

    try {
        await createUserWithEmailAndPassword(auth, email, password);
        const sendVerificationEmailFn = httpsCallable(functions, 'sendVerificationEmail');
        await sendVerificationEmailFn({
            email: email,
            origin: window.location.origin
        });
        navigate('/verification');
    } catch (error: unknown) {
        console.error(error);
        const firebaseError = error as { code?: string; message?: string };
        if (firebaseError.code === 'auth/email-already-in-use') {
            setError('このメールアドレスは既に登録されています。ログインをお試しください。');
        } else {
            setError('アカウント作成に失敗しました：' + (firebaseError.message || '不明なエラー'));
        }
    } finally {
        setIsLoading(false);
    }
  };

  const ValidationItem: React.FC<{ fulfilled: boolean; text: string }> = ({ fulfilled, text }) => (
    <div className={`flex items-center space-x-2 text-xs ${fulfilled ? 'text-blue-500 font-bold' : 'text-gray-400'}`}>
      <span className={`flex items-center justify-center w-4 h-4 rounded-[2px] border ${fulfilled ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300 text-transparent'}`}>
        <Check size={10} strokeWidth={4} />
      </span>
      <span>{text}</span>
    </div>
  );

  return (
    <AuthLayout>
      <div className="w-full max-w-sm mx-auto">
          <div className="flex justify-center mb-12 lg:hidden">
              <Logo size="lg" />
          </div>

          <h1 className="text-2xl font-bold text-slate-800 mb-10 text-center">無料アカウント登録</h1>

          <form onSubmit={handleSubmit}>
            <Input
              label="メールアドレス"
              requiredLabel
              placeholder="sample@email.com"
              type="email"
              value={email}
              onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
              }}
              error={error && error.includes('メールアドレス') ? '' : undefined}
            />

            <div className="mb-6">
                <Input
                    label="パスワード"
                    requiredLabel
                    placeholder="半角英数8文字" 
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mb-0"
                />
                <div className="mt-3 space-y-1.5">
                    <ValidationItem fulfilled={validations.length} text="8文字以上" />
                    <ValidationItem fulfilled={validations.english} text="英文字（大文字・小文字）を含む" />
                    <ValidationItem fulfilled={validations.numberOrSymbol} text="数字または記号を含む" />
                </div>
            </div>

            <div className="mb-8 flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="w-5 h-5 border border-gray-300 rounded-[2px] text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
              </div>
              <div className="ml-3 text-xs leading-5">
                <label htmlFor="terms" className="font-medium text-gray-500">
                  <a href="https://lp.shiftmate.jp/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-blue-500 font-bold hover:underline">利用規約</a>
                  と
                  <a href="https://lp.shiftmate.jp/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-500 font-bold hover:underline">プライバシーポリシー</a>
                  に同意の上、登録します。
                </label>
              </div>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-[4px] flex items-start text-red-600 text-sm font-bold animate-fade-in-up">
                    <AlertCircle className="flex-shrink-0 mr-2 mt-0.5" size={18} />
                    <span>{error}</span>
                </div>
            )}

            <button
                type="submit"
                disabled={!agreed || isLoading}
                className="w-full py-4 bg-blue-600 text-white font-bold rounded-[4px] shadow-md hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
                {isLoading ? <Loader2 className="animate-spin" size={20} /> : 'アカウント登録'}
            </button>
          </form>

          <div className="mt-12 border-t border-gray-200 pt-6 text-center">
            <span className="text-sm text-slate-600">すでに登録済みの方は</span>
            <span 
                onClick={() => navigate('/login')} 
                className="cursor-pointer text-sm text-blue-500 border-b border-blue-500 ml-1 hover:text-blue-700 hover:border-blue-700 pb-0.5 transition-colors font-bold"
            >
                こちら
            </span>
          </div>
      </div>
    </AuthLayout>
  );
};

export default RegisterPage;